<template>
  <div class="py-12">
    <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
      <div
        v-for="(examination, i) in examinations"
        :key="i">
        <accordion>
          <template #title>
            <h1 class="text-xl">{{ examination.title }}</h1>
          </template>
          <template #content>
            <h1 class="text-lg font-bold">受験者数</h1>
            <p class="mb-2">{{ totalUserNum(examination.id) }}名</p>
            <h1 class="text-lg font-bold">種別の合計</h1>
            <ul class="">
              <li>最低点: {{ getTotalLowScore(examination.id) }}/200</li>
              <li>平均点: {{ getTotalAverageScore(examination.id) }}/200</li>
              <li>最高点: {{ getTotalHighestScore(examination.id) }}/200</li>
            </ul>
            <div class="flex mt-12">
              <BarChart
                ref="barRef"
                class="w-2/5"
                :chart-data="getExaminationPercentageData(examination.id)"
                :options="barOptions" />

              <BarChart
                ref="barRef"
                class="w-3/5"
                :chart-data="getSubjectCorrentAnswerRate(examination.id)"
                :options="barOptions" />
            </div>

            <div class="max-h-96 mb-4 w-full overflow-y-auto mt-12 scrollbar scrollbar-thumb-rounded scrollbar-thumb-black">
              <table class="table-auto w-full">
                <tr class="sticky -top-0.5 bg-gray-300">
                  <th class="text-center border w-24 px-1 py-2">
                    ID
                  </th>
                  <th class="text-center border px-4 py-2 w-24">
                    正答率
                  </th>
                  <th class="text-center border px-4 py-2">
                    問題文
                  </th>
                </tr>
                <tr
                  v-for="question in questionCorrentAnswerRate"
                  :key="question.id">
                  <td
                    v-if="question.examination_id === examination.id"
                    class="text-right border px-1 py-2">
                    {{ question.question_id }}
                  </td>
                  <td
                    v-if="question.examination_id === examination.id"
                    class="border px-4 py-2 text-right">
                    {{ question.correct_answer_rate }}%
                  </td>
                  <td
                    v-if="question.examination_id === examination.id"
                    class="border px-4 py-2">
                    {{ question.question }}
                  </td>
                </tr>
              </table>
            </div>
          </template>
        </accordion>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  defineProps,
  ref,
  computed,
} from 'vue'
import { Chart, registerables } from 'chart.js'
import {
  LineChart, BarChart, PieChart, Utils,
} from 'vue-chart-3'
import Accordion from './Accordion'

const props = defineProps({
  examinationScore: {
    type: Array,
    default: () => [],
  },
  questionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  subjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  examinations: {
    type: Array,
    default: () => [],
  },
  userExaminationProgresses: {
    type: Array,
    default: () => [],
  },
})

Chart.register(...registerables)

const showTabDisplay = ref(0)
const changeTab = (num) => {
  showTabDisplay.value = num
}

const isOpen = ref()

const toggleAccordion = () => {
  isOpen.value = !isOpen.value
}

const totalUserNum = (examId) => props.userExaminationProgresses.filter(el => el.examination_id === examId).length

const barRef = ref()
const barOptions = ref({
  responsive: true,
  plugins: {
    legend: {
      labels: {
        font: {
          size: 16,
        },
      },
    },
  },
  responsive: true,
})

const getTotalLowScore = computed(() => (id) => {
  const examinationScore = props.examinationScore.filter(exam => exam.examination_id === id)
  const totalLowScore = examinationScore[0]?.lowest_score + examinationScore[1]?.lowest_score + examinationScore[2]?.lowest_score

  return totalLowScore || 0
})

const getTotalAverageScore = computed(() => (id) => {
  const examinationScore = props.examinationScore.filter(exam => exam.examination_id === id)
  const totalAverageScore = examinationScore[0]?.average_score + examinationScore[1]?.average_score + examinationScore[2]?.average_score

  return totalAverageScore || 0
})

const getTotalHighestScore = computed(() => (id) => {
  const examinationScore = props.examinationScore.filter(exam => exam.examination_id === id)
  const totalHighestScore = examinationScore[0]?.highest_score + examinationScore[1]?.highest_score + examinationScore[2]?.highest_score

  return totalHighestScore || 0
})

const getExaminationPercentageData = computed(() => (id) => {
  const newExaminationScore = props.examinationScore.filter(exam => exam.examination_id === id)
  const chartData = {
    labels: ['必須', '一般', '実地'],
    datasets: [
      {
        label: '最低点(%)',
        data: [
          newExaminationScore[0]?.lowest_score * 2,
          newExaminationScore[1]?.lowest_score,
          newExaminationScore[2]?.lowest_score * 2,
        ],
        backgroundColor: '#C0392B',
      },
      {
        label: '平均点(%)',
        data: [
          newExaminationScore[0]?.average_score * 2,
          newExaminationScore[1]?.average_score,
          newExaminationScore[2]?.average_score * 2,
        ],
        backgroundColor: '#F5B041',
      },
      {
        label: '最高点(%)',
        data: [
          newExaminationScore[0]?.highest_score * 2,
          newExaminationScore[1]?.highest_score,
          newExaminationScore[2]?.highest_score * 2,
        ],
        backgroundColor: '#2ECC71',
      },
    ],
  }

  return chartData
})

const getSubjectCorrentAnswerRate = computed(() => (id) => {
  const newSubjectCorrentAnswerRate = props.subjectCorrentAnswerRate.filter(exam => exam.examination_id === id)
  const chartData = {
    labels: ['基礎動物学', '基礎動物看護学', '臨床動物看護学', '愛護・適正飼養学', '実習'],
    datasets: [
      {
        label: '科目ごとの正答率',
        data: [
          newSubjectCorrentAnswerRate[0]?.correct_answer_rate,
          newSubjectCorrentAnswerRate[1]?.correct_answer_rate,
          newSubjectCorrentAnswerRate[2]?.correct_answer_rate,
          newSubjectCorrentAnswerRate[3]?.correct_answer_rate,
          newSubjectCorrentAnswerRate[4]?.correct_answer_rate,
        ],
        backgroundColor: '#2ECC71',
      },
    ],
  }

  return chartData
})
</script>
